import { useCallback, useContext, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useBulkActionsState } from 'src/components/common/BulkActions/useBulkActionsState';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { Button, DropdownMenu } from 'src/core/ui';
import { useRetailSaleListingTickets } from 'src/hooks/api/useRetailSaleListingTickets';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import * as loadingStyles from 'src/modals/RetailSale/components/MultiListingRetailSaleCheckoutStep.css';
import { RetailSaleWithProvider } from 'src/modals/RetailSale/RetailSale';
import { getRetailExternalSaleId } from 'src/modals/RetailSale/RetailSaleLocalContext.utils';
import {
  MultiListingSelectionRow,
  RetailSaleFormValues,
} from 'src/modals/RetailSale/types';
import { ContentId } from 'src/utils/constants/contentId';
import { EmptyListingQuery } from 'src/utils/eventQueryUtils';
import {
  Feature,
  ListingQuery,
  RetailSaleListingTickets,
  Ticket,
  TicketType,
} from 'src/WebApiController';

import { useInventoryEventBulkActionsState } from '../../InventoryEventBulkActions/useInventoryEventBulkActionsState';

/**
 * Bulk action that opens the RetailSale modal in multi-listing mode
 * (POS-8649 — Sales Group multi-event checkout).
 *
 * Button enablement mirrors the other bulk actions — gated on
 * `areBulkActionsEnabled` alone, no ID lookups. All resolving of
 * listing details is deferred to the modal so the inventory page
 * doesn't burn API calls on every unrelated selection change.
 *
 * Click flow:
 *   1. Fast path: use `affectedVisibleListingIds` if already populated
 *      (individual listings selected, or events the user has expanded).
 *      Build a ListingQuery with entityIds and pass it to the modal.
 *   2. Slow path: for event-only selections, pass filterQueryWithEventIds
 *      directly. The modal fetches all listings via a single batch call to
 *      GetListingsSectionalDataForEvents.
 */
export const RetailSaleActions = () => {
  const hasRetailSaleFeature = useUserHasFeature(Feature.RetailSale);
  const { areBulkActionsEnabled } = useBulkActionsState();
  const { setModal } = useContext(ModalContext);

  const { affectedVisibleListingIds, filterQueryWithEventIds } =
    useInventoryEventBulkActionsState();

  const handleClick = useCallback(() => {
    if (!areBulkActionsEnabled) return;

    // Fast path: individual listings already resolved from catalog context.
    // Slow path: event-only selection — pass the event filter so the modal
    // fetches all listings via GetListingsSectionalDataForEvents.
    const filterQuery: ListingQuery =
      affectedVisibleListingIds.length > 0
        ? { ...EmptyListingQuery, entityIds: affectedVisibleListingIds }
        : filterQueryWithEventIds;

    // Guard: nothing selected
    if (
      (filterQuery.entityIds == null || filterQuery.entityIds.length === 0) &&
      (filterQuery.eventOrMappingIds == null ||
        filterQuery.eventOrMappingIds.length === 0)
    ) {
      return;
    }

    setModal({
      children: <MultiListingRetailSaleModal filterQuery={filterQuery} />,
      clickOutsideToClose: true,
      size: 'slide-in',
    });
  }, [
    affectedVisibleListingIds,
    areBulkActionsEnabled,
    filterQueryWithEventIds,
    setModal,
  ]);

  if (!hasRetailSaleFeature) {
    return null;
  }

  const isDisabled = !areBulkActionsEnabled;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild disabled={isDisabled}>
        <Button variant="outline" disabled={isDisabled}>
          <Content id={ContentId.RetailSale} />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="start">
          <DropdownMenu.Item onClick={handleClick}>
            <Content id={ContentId.MultiEventCheckout} />
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

/**
 * Multi-listing entry point. Mirrors the single-listing `<RetailSale>`
 * component in RetailSale.tsx: mount `<FormProvider>` synchronously with
 * empty defaults, then `methods.reset(...)` in a useEffect once listing
 * details have loaded. Keeping the FormProvider tree stable across the
 * loading transition matters — the Adyen Drop-In initiates a fetch inside
 * a useEffect after the form user hits Pay & Complete, and any remount of
 * the subtree while that fetch is in flight tears it down before it can
 * receive its config, producing `TypeError: Failed to fetch` on the
 * checkoutshopper session-setup call.
 */
const MultiListingRetailSaleModal = ({
  filterQuery,
}: {
  filterQuery: ListingQuery;
}) => {
  const { data: listingDetails, isLoading } =
    useRetailSaleListingTickets(filterQuery);

  const methods = useForm<RetailSaleFormValues>({
    defaultValues: {},
  });

  useEffect(() => {
    if (!listingDetails || listingDetails.length === 0) return;

    const perListingSelections = listingDetails.flatMap((ld) =>
      buildPerListingSelectionsForListing(ld)
    );

    const ticketIds = perListingSelections.flatMap((sel) =>
      sel.availableTicketIds.slice(0, sel.qtyToSell)
    );

    const allListingIds = listingDetails.map((ld) => ld.listingId);
    const primaryListingId = allListingIds[0] ?? 0;
    const quantitySold = ticketIds.length > 0 ? ticketIds.length : undefined;

    // totalNetProceeds seed = Σ(qtyToSell × unitProceeds). Source of truth
    // in multi-listing mode (RetailSaleBuyerStep hides its manual
    // totalNetProceeds input); MultiListingRetailSaleCheckoutStep keeps
    // it reactive as the seller edits per-row values.
    const totalNetProceedsSum = perListingSelections.reduce(
      (sum, sel) => sum + sel.qtyToSell * sel.unitProceeds,
      0
    );
    const totalNetProceeds =
      totalNetProceedsSum > 0 ? totalNetProceedsSum : undefined;

    const primaryListing = listingDetails.find(
      (ld) => ld.listingId === primaryListingId
    );
    const ticketType =
      primaryListing?.topPriTktType ??
      listingDetails[0]?.topPriTktType ??
      TicketType.ETicket;
    const currencyCode =
      primaryListing?.currency ?? listingDetails[0]?.currency ?? 'USD';

    methods.reset({
      listingId: primaryListingId,
      // POS-8649 — Bug fix #4: seed externalSaleId with an empty string so
      // react-hook-form's register() never warns about an uncontrolled input.
      externalSaleId: primaryListingId
        ? getRetailExternalSaleId(primaryListingId)
        : '',
      // ticketIds presence marks multi-listing mode (see
      // RetailSaleLocalContext.tsx bug fix #2).
      ticketIds,
      allListingIds,
      perListingSelections,
      currencyCode,
      saleDate: new Date().toISOString(),
      useShippingAddressOnFile: false,
      billingAddressSameAsShipping: true,
      marketplacePaymentLines: [],
      ticketType,
      quantitySold,
      totalNetProceeds,
      paymentMethodChoice: 'adyen',
      externalPaymentMethodType: null,
      externalPaymentNote: null,
      externalPaymentAmount:
        totalNetProceeds != null && totalNetProceeds > 0
          ? totalNetProceeds
          : null,
      externalPaymentDate: new Date().toISOString().split('T')[0],
    });
  }, [listingDetails, methods]);

  if (isLoading || !listingDetails || listingDetails.length === 0) {
    return (
      <div className={loadingStyles.modalLoading}>
        <PosSpinner />
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <RetailSaleWithProvider />
    </FormProvider>
  );
};

/**
 * Groups a listing's tickets by (section, row) and returns one
 * MultiListingSelectionRow per group. Grouping key follows the same
 * convention as the previous #5707 prototype's inline logic: section
 * from `ld.seating.section`, row from `ticket.row ?? ld.seating.row`.
 *
 * Within each group tickets are ordered by `lstOrd` so the seat-range
 * autofill and the `availableTicketIds.slice(0, qtyToSell)` derivation
 * stay consistent with `getConsecutiveSeatingsForQuantity` (which sorts
 * by `lstOrd`).
 *
 * Default `qtyToSell` = group size (broker's full selection); if they
 * lower the number in the table, the submit path trims from the end.
 */
const buildPerListingSelectionsForListing = (
  ld: RetailSaleListingTickets
): MultiListingSelectionRow[] => {
  const listingId = ld.listingId;
  const ldSection = ld.seating?.section ?? null;
  const ldRow = ld.seating?.row ?? null;

  const groups: Record<
    string,
    { section: string | null; row: string | null; tickets: Ticket[] }
  > = {};

  // Only include tickets that can actually be sold. Mirrors resolveSaleTicketIds
  // in RetailSaleLocalContext.utils.ts — a listing's tickets array still returns
  // sold/held/spoiled/wasted rows, and the backend rejects the sales-group create
  // with "Tickets <id> are not available for allocation" if we forward them.
  const availableTickets = (ld.tickets ?? []).filter(
    (t) =>
      t.saleId == null &&
      t.holdId == null &&
      t.spoiledSaleId == null &&
      !t.isWasted
  );

  availableTickets.forEach((t) => {
    const section = ldSection;
    const row = t.row ?? ldRow;
    const key = `${listingId}||${section ?? ''}||${row ?? ''}`;
    if (!groups[key]) {
      groups[key] = { section, row, tickets: [] };
    }
    groups[key].tickets.push(t);
  });

  const unitProceedsSeed =
    ld.listPrice || ld.allInPrice || ld.unitCst?.amt || 0;

  return Object.values(groups).map((g): MultiListingSelectionRow => {
    const sortedTickets = g.tickets.toSorted(
      (a, b) => (a.lstOrd ?? a.ord) - (b.lstOrd ?? b.ord)
    );
    const availableTicketIds = sortedTickets
      .map((t) => t.id)
      .filter((id): id is number => id > 0);
    const firstSeat = sortedTickets[0]?.seat ?? null;
    const lastSeat = sortedTickets[sortedTickets.length - 1]?.seat ?? null;
    return {
      listingId,
      section: g.section,
      row: g.row,
      availableTicketIds,
      qtyToSell: availableTicketIds.length,
      seatFrom: firstSeat,
      seatTo: lastSeat,
      unitProceeds: unitProceedsSeed,
    };
  });
};
